import { Injectable } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, debounceTime, filter, map, tap } from 'rxjs';

import { NumpadDisplayService } from '../../../../lib/numpad/numpad-display.service';
import { selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { getPicksCount, isValidOnePickCombo } from '../../../core/utils';
import { BetslipBarVisibilityService } from '../../betslip-bar/services/betslip-bar-visibility.service';
import BetslipComboBetActions from '../../combo-bet/actions';
import BetslipPickActions from '../../picks/actions';
import { removePicksFromGroup } from '../../picks/grouped-picks.actions';
import { ofTypeReplaceExistingPick } from '../../picks/helpers';
import { betslipPicksListCountSelector, betslipPicksListSelector, selectBetBuilderPicksInfo } from '../../picks/selectors';
import BetslipPickUtilsService from '../../picks/services/betslip-pick-utils.service';
import BetslipSingleBetActions from '../../single-bet/actions';
import BetslipSystemBetActions from '../../system-bet/actions';
import { BetslipTeaserActions } from '../../teaser-bet/actions';
import { teaserBetPickTeasersSelector } from '../../teaser-bet/selectors';
import BetValidationService from '../../validation/services/composition/bets/bet-validation.service';
import { BetslipTypesActions } from '../actions';
import { betslipSelectedTypeStateSelector, betslipTypeStateSelector } from '../selectors';

@Injectable()
export default class DigitalTabsSelectionEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private betValidationService: BetValidationService,
        private numpadDisplayService: NumpadDisplayService,
        private betslipPickUtils: BetslipPickUtilsService,
        private betslipBarVisibility: BetslipBarVisibilityService,
        private betslipConfig: BetslipConfig,
    ) {}

    autoSwitchToComboOnPickReplace$ = createEffect(() => {
        return this.actions$.pipe(
            ofTypeReplaceExistingPick(),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            filter(
                ([action, picks]) =>
                    action.pickId.isEqual(action.newPick.id) && getPicksCount(picks) > 1 && picks.every((t) => !BetslipUnknownPick.isPick(t)),
            ),
            map(() => BetslipTypesActions.triggerAutoComboSwitch()),
        );
    });

    autoSwitchToComboNewPickIdOnReplace$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                BetslipComboBetActions.addPickWithNewPickId,
                BetslipSingleBetActions.addPickWithNewPickId,
                BetslipSystemBetActions.addPickWithNewPickId,
            ),
            debounceTime(1),
            map(() => BetslipTypesActions.triggerAutoComboSwitch()),
        );
    });

    autoSwitchToComboOnPickRemoval$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => this.store.select(betslipPicksListSelector)),
            filter(([_, picks]) => getPicksCount(picks) > 1 && picks.every((t) => !BetslipUnknownPick.isPick(t))),
            //this was added to prevent racing conditions between bet type state and pick list state
            //add debounce to avoid redundant effect triggers in case multiple remove picks were triggered in short cycles (removing picks when creating a group for example)
            debounceTime(5),
            map(() => BetslipTypesActions.triggerAutoComboSwitch()),
        );
    });

    /**
     * We want to switch to combo bet if current selection of picks has no combo errors,
     * When otherwise and we have been on combo switch back to single bet.
     */
    autoSwitchToCombo$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipTypesActions.triggerAutoComboSwitch),
            concatLatestFrom(() => [
                this.store.select(betslipPicksListSelector),
                this.store.select(betslipTypeStateSelector),
                this.betslipBarVisibility.enableDsl$,
                this.store.select(selectBetBuilderPicksInfo),
                this.store.select(selectIsLinearBetslip),
            ]),
            filter(
                ([, , { base }]) =>
                    !base.selectedByUser && (base.currentSelectedType === BetslipType.Single || base.currentSelectedType === BetslipType.Combo),
            ),
            concatMap(function* ([, picks, typesState, betslipBarEnabled, betBuilderPicksState, isLinear]): Iterable<Action> {
                const isLinearCombo = isLinear && typesState.base.linearTypes.COMBO;

                const selectedPicks = isLinearCombo
                    ? // For linear we want to keep the state as combo even if one or more picks are unselected
                      // Counting only selected picks causes issues where the Linear Combo module might be shown but the state is set to single internally
                      picks
                    : // we are getting selected picks and groups because we want to deal with groups as if they were always selected (to avoid infinity loop in this effect)
                      effect.betslipPickUtils.getSelectedPicksAndGroups(typesState, picks, effect.betslipConfig.isSportcastAsComboEnabled);

                const hasComboPreventionErrors = !effect.betValidationService.checkComboBet(selectedPicks);
                const isValidOnePickComboState = isValidOnePickCombo(selectedPicks, effect.betslipConfig.isSportcastAsComboEnabled);
                const hasValidCount = getPicksCount(selectedPicks) > 1 || isValidOnePickComboState;
                const betBarShouldShowCombo = hasValidCount && betslipBarEnabled;

                if (
                    ((!hasComboPreventionErrors && hasValidCount) || betBarShouldShowCombo) &&
                    typesState.base.currentSelectedType === BetslipType.Single
                ) {
                    // Single bet selected and has not errors  => select combo tab
                    yield BetslipTypesActions.setSelectedType({
                        betslipType: BetslipType.Combo,
                        selectedByUser: false,
                        systemInfo: null,
                    });
                }
                if (
                    (hasComboPreventionErrors || !hasValidCount) &&
                    !betBarShouldShowCombo &&
                    typesState.base.currentSelectedType === BetslipType.Combo &&
                    (!effect.betslipConfig.isSportcastAsComboEnabled || !betBuilderPicksState.hasOnlyBetBuilderPicks)
                ) {
                    // Combo bet selected and has errors  => select single tab
                    yield BetslipTypesActions.setSelectedType({
                        betslipType: BetslipType.Single,
                        selectedByUser: false,
                        systemInfo: null,
                    });
                }
            }),
        );
    });

    /**
     * When "Sportcast as Combo" is enabled we want to switch to combo bet if current the  tab doesn't have any picks while the combo has some BetBuilderPicks because otherwise we're staying on an empty tab.
     */
    autoSwitchToComboWhenOnlyBetBuilderPicks$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            filter(() => effect.betslipConfig.isSportcastAsComboEnabled),
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks),
            concatLatestFrom(() => this.store.select(betslipSelectedTypeStateSelector)),
            filter(([_, currentBetslipType]) => currentBetslipType === BetslipType.Single || currentBetslipType === BetslipType.System),
            concatLatestFrom(() => this.store.select(selectBetBuilderPicksInfo)),
            filter(([_, betBuilderPicksState]) => !betBuilderPicksState.nonBetBuilderPicksCount && !!betBuilderPicksState.betBuilderPicksCount),
            //add debounce to avoid redundant effect triggers in case multiple remove picks were triggered in short cycles (removing picks when creating a group for example)
            debounceTime(5),
            map(() =>
                BetslipTypesActions.setSelectedType({
                    betslipType: BetslipType.Combo,
                    selectedByUser: false,
                    systemInfo: null,
                }),
            ),
        );
    });

    autoSwitchToSingle$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, removePicksFromGroup, BetslipPickActions.removeMultiplePicks),
            //add debounce to avoid redundant effect triggers in case multiple remove picks were triggered in short cycles (removing picks when creating a group for example)
            debounceTime(5),
            concatLatestFrom(() => this.store.select(selectBetBuilderPicksInfo)),
            concatLatestFrom(() => this.store.select(betslipPicksListCountSelector)),
            map(([[_, betBuilderPicksState], length]) =>
                effect.betslipConfig.isSportcastAsComboEnabled
                    ? betBuilderPicksState.nonBetBuilderPicksCount === 1 && betBuilderPicksState.betBuilderPicksCount === 0
                    : length === 1,
            ),
            filter((isValidPickCountForSingle) => isValidPickCountForSingle),
            concatLatestFrom(() => this.store.select(betslipTypeStateSelector)),
            filter(([_, types]) => !types.base.currentSelectedType || !types.base.selectedByUser),
            map(() =>
                BetslipTypesActions.setSelectedType({
                    betslipType: BetslipType.Single,
                    selectedByUser: false,
                    systemInfo: null,
                }),
            ),
        );
    });

    autoSwitchToSingleFromTeaser$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(BetslipPickActions.removePick, BetslipPickActions.removeMultiplePicks, BetslipTeaserActions.removePick),
            concatLatestFrom(() => this.store.select(teaserBetPickTeasersSelector)),
            filter(([action, state]) => state.currrentSelectedType === BetslipType.Teaser && Object.entries(state.teaserBetsState.picks)?.length < 2),
            concatMap(function* (): Iterable<Action> {
                yield BetslipTypesActions.setSelectedType({
                    betslipType: BetslipType.Single,
                    selectedByUser: false,
                    systemInfo: null,
                });
                yield BetslipTeaserActions.setTeasePicks({ keypoint: 0 });
            }),
        );
    });

    closeNumpadOnTypeChange$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(BetslipTypesActions.setSelectedType),
                filter(() => this.numpadDisplayService.isOpen()),
                tap(() => {
                    this.numpadDisplayService.close();
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );
}
